import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import type { PlatformSurvey, PlatformSurveyQuestion } from "./usePlatformSurveys";

export type PlatformSurveyAssignment = {
  id: string;
  survey_id: string;
  user_id: string;
  cohort_id: string | null;
  wave_label: string;
  due_at: string | null;
  notified_at: string | null;
  assigned_at: string;
};

export type PlatformSurveyResponse = {
  id: string;
  assignment_id: string;
  survey_id: string;
  user_id: string;
  started_at: string | null;
  completed_at: string | null;
};

export type PlatformSurveyAnswer = {
  id?: string;
  response_id: string;
  question_id: string;
  answer_text?: string | null;
  answer_number?: number | null;
  answer_choice?: any | null;
};

export function useSurveyAssignment(assignmentId: string | undefined) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["survey_assignment", assignmentId, user?.id],
    enabled: !!assignmentId && !!user?.id,
    queryFn: async () => {
      const { data: assignment, error: aErr } = await supabase
        .from("platform_survey_assignments")
        .select("*")
        .eq("id", assignmentId!)
        .eq("user_id", user!.id)
        .maybeSingle();
      if (aErr) throw aErr;
      if (!assignment) {
        return { assignment: null, survey: null, questions: [] as PlatformSurveyQuestion[] };
      }

      const [sRes, qRes] = await Promise.all([
        supabase.from("platform_surveys").select("*").eq("id", assignment.survey_id).maybeSingle(),
        supabase
          .from("platform_survey_questions")
          .select("*")
          .eq("survey_id", assignment.survey_id)
          .order("display_order", { ascending: true }),
      ]);
      if (sRes.error) throw sRes.error;
      if (qRes.error) throw qRes.error;

      return {
        assignment: assignment as PlatformSurveyAssignment,
        survey: (sRes.data || null) as PlatformSurvey | null,
        questions: (qRes.data || []) as PlatformSurveyQuestion[],
      };
    },
  });
  return {
    assignment: query.data?.assignment ?? null,
    survey: query.data?.survey ?? null,
    questions: query.data?.questions ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useSurveyResponse(
  assignmentId: string | undefined,
  surveyId: string | undefined,
  waveLabel: string | undefined,
) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["survey_response", assignmentId, user?.id],
    enabled: !!assignmentId && !!surveyId && !!user?.id && waveLabel !== undefined,
    queryFn: async (): Promise<{
      response: PlatformSurveyResponse;
      answers: PlatformSurveyAnswer[];
    }> => {
      const { data: existing, error: rErr } = await supabase
        .from("platform_survey_responses")
        .select("*")
        .eq("assignment_id", assignmentId!)
        .eq("user_id", user!.id)
        .maybeSingle();
      if (rErr) throw rErr;

      let response = existing as PlatformSurveyResponse | null;
      if (!response) {
        const { data: created, error: cErr } = await supabase
          .from("platform_survey_responses")
          .insert({
            assignment_id: assignmentId!,
            survey_id: surveyId!,
            user_id: user!.id,
            wave_label: waveLabel ?? "",
            started_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (cErr) throw cErr;
        response = created as PlatformSurveyResponse;
      }

      const { data: answers, error: aErr } = await supabase
        .from("platform_survey_answers")
        .select("*")
        .eq("response_id", response.id);
      if (aErr) throw aErr;

      return { response, answers: (answers || []) as PlatformSurveyAnswer[] };
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["survey_response", assignmentId, user?.id] });

  return {
    response: query.data?.response ?? null,
    answers: query.data?.answers ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refresh,
  };
}

export function useUpsertSurveyAnswer() {
  return useMutation({
    mutationFn: async (input: PlatformSurveyAnswer) => {
      const payload = {
        response_id: input.response_id,
        question_id: input.question_id,
        answer_text: input.answer_text ?? null,
        answer_number: input.answer_number ?? null,
        answer_choice: input.answer_choice ?? null,
      };
      const { data, error } = await supabase
        .from("platform_survey_answers")
        .upsert(payload, { onConflict: "response_id,question_id" })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useCompleteSurveyResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (responseId: string) => {
      const { data, error } = await supabase
        .from("platform_survey_responses")
        .update({ completed_at: new Date().toISOString() })
        .eq("id", responseId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["survey_response"] });
      qc.invalidateQueries({ queryKey: ["pending_surveys"] });
    },
  });
}
